export const SITE = {
  name: "Drammen Car Wash",
  city: "Drammen",
  address: "Nedre Eikervei 47, 3045 Drammen, Norge",
  phone: "973 13 691",
  phoneHref: "tel:+4797313691",
  email: "drammen.car.wash@gmail.com",
  rating: 4.8,
  /** Reserve. Forsiden og JSON-LD leser de ekte tallene fra Ascendly
   *  (reviewTracking:publicReviewSummary, synket fra Google hver morgen); dette
   *  brukes bare hvis Convex ikke svarer. Oppdatert 2. oktober 2026 (4,8 av 42). */
  reviewCount: 42,
  googleReviewUrl: "https://review.drammencarwash.no",
  facebookUrl: "https://www.facebook.com/people/Drammen-Car-Wash/61593209130555/",
  url: "https://drammencarwash.no",
};

/** Nearby areas served from the Drammen location, used for local SEO copy and area pages. */
export const NEARBY_AREAS = ["Mjøndalen", "Konnerud", "Lier", "Åssiden", "Krokstadelva", "Nedre Eiker", "Kongsberg"];

export type CarSize = "liten" | "stor";

export type Service = {
  id: string;
  name: string;
  desc: string;
  /** null = "etter avtale" (no fixed price) */
  priceSmall: number | null;
  priceLarge: number | null;
  /** true if the price is a starting price ("fra") rather than a fixed one */
  fromPrice?: boolean;
  featured?: boolean;
  /** estimated treatment time, used to filter out time slots that don't fit before closing */
  durationMinutes: number;
  /** itemized breakdown shown on the booking page for standout packages */
  includes?: string[];
};

export type ServiceId = string;

export const SERVICES: readonly Service[] = [
  { id: "innvendig", name: "Innvendig vask", priceSmall: 400, priceLarge: 500, durationMinutes: 45,
    desc: "Effektiv støvsuging, rengjøring av alle overflater, alle vinduer og matter." },
  { id: "utvendig", name: "Utvendig vask", priceSmall: 250, priceLarge: 300, durationMinutes: 30,
    desc: "Forvask med avfetting, skumvask, håndvask, felgrens og tørking." },
  { id: "komplett", name: "Komplett inn- & utvendig vask", priceSmall: 600, priceLarge: 700, featured: true, durationMinutes: 60,
    desc: "Alt fra innvendig vask og alt fra utvendig vask." },
  { id: "premium", name: "Premiumpakke", priceSmall: 1000, priceLarge: 1200, durationMinutes: 90,
    desc: "Innvendig vask med tornado-maskin, utvendig vask og motorromrens." },
  { id: "motorromrens", name: "Motorromrens", priceSmall: 250, priceLarge: 350, durationMinutes: 30,
    desc: "Varsom rengjøring som fjerner støv, olje og smuss." },
  { id: "felgrens", name: "Felgrens", priceSmall: 150, priceLarge: 250, durationMinutes: 20,
    desc: "Grundig rens som gir felgene tilbake glansen." },
  { id: "dekkskift", name: "Dekkskift", priceSmall: 350, priceLarge: 450, durationMinutes: 30,
    desc: "Nøyaktig, profesjonelt og raskt dekkskift." },
  { id: "oljeskift", name: "Oljeskift", priceSmall: 350, priceLarge: 350, fromPrice: true, durationMinutes: 30,
    desc: "Oljeskift med eller uten filter." },
  { id: "rustfjerning", name: "Rustfjerning / Asfaltfjerning", priceSmall: 500, priceLarge: 600, fromPrice: true, durationMinutes: 60,
    desc: "Fjerner rust- og asfaltsprut skånsomt fra lakken." },
  { id: "polering-enkel", name: "Enkel polering", priceSmall: null, priceLarge: null, durationMinutes: 60,
    desc: "Fjerner små riper på utvalgte steder." },
  { id: "gull-komplett", name: "Gull komplett", priceSmall: 2500, priceLarge: 3500, durationMinutes: 180,
    desc: "Polering av hele bilen, innvendig og utvendig vask, damprens og fjerning av flekker på setene, motorromrens.",
    includes: [
      "Polering av hele bilen",
      "Innvendig vask",
      "Utvendig vask",
      "Damprens",
      "Fjerning av flekker på setene",
      "Motorromrens",
    ] },
];

export function priceForSize(s: Service, size: CarSize): number {
  const p = size === "liten" ? s.priceSmall : s.priceLarge;
  return p ?? 0;
}

export function priceRangeLabel(s: Service): string {
  if (s.priceSmall == null || s.priceLarge == null) return "Etter avtale";
  const prefix = s.fromPrice ? "fra " : "";
  if (s.priceSmall === s.priceLarge) return `${prefix}${s.priceSmall} kr`;
  return `${prefix}${s.priceSmall}–${s.priceLarge} kr`;
}

/** Shape of a row from Convex's `services` table, as returned by `bookings.listServicesForBusiness`. */
export type LiveServiceRow = {
  _id?: string;
  name: string;
  durationMinutes?: number;
  price?: number;
  priceLarge?: number;
  fromPrice?: boolean;
  description?: string;
  includes?: string[];
  active?: boolean;
  bestselger?: boolean;
  /** Tillegg (f.eks. felgrens ved siden av en vask), ikke en tjeneste i seg selv. */
  isAddon?: boolean;
  kategori?: string;
  imageUrl?: string | null;
};

/**
 * Lesbar id/slug av et tjenestenavn fra Ascendly, f.eks. «Seterens» → «seterens»
 * og «Vask av båt» → «vask-av-bat». Navnet er noekkelen mot Ascendly allerede,
 * saa denne holder som id i lenker, i bookingens ?service= og i /tjenester/<slug>.
 */
export function slugForServiceName(name: string): string {
  return name
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "o")
    .replace(/å/g, "a")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Overlays live Convex service data (price, description, includes, duration, "mest populaer")
 * onto the local SERVICES list, matched by exact `name`. `id` still comes from the local list,
 * and this only overrides fields on services that already exist locally; it never changes
 * the order. While `live` is undefined (query still loading), the local list is returned
 * unchanged.
 *
 * `featured` er et unntak fra "felt for felt"-regelen. Saa snart eieren har huket av "mest
 * populaer" paa minst én tjeneste i appen, er det den avkryssingen som gjelder for alle, ogsaa
 * de som ikke er huket av. Ellers ville den lokale `featured: true` paa komplettpakken ligge
 * igjen ved siden av eierens eget valg, og siden ville vist to "Mest populaer"-merker samtidig.
 * Har ingen huket av noe, gjelder den lokale verdien som foer.
 *
 * En tjeneste eieren har skrudd av i Ascendly (`active: false`) tas ut av lista, ogsaa naar
 * den finnes lokalt. Foer ble bare prisen lest, og en avskrudd tjeneste laa igjen paa
 * nettsiden med gammel pris som om ingenting var gjort.
 */
export function mergeLiveServices(
  local: readonly Service[],
  live: readonly LiveServiceRow[] | undefined,
): Service[] {
  if (!live || live.length === 0) return local as Service[];
  const byName = new Map(live.map((row) => [row.name, row]));
  const eierenHarValgt = live.some((row) => row.bestselger === true);

  const fraLokal: Service[] = [];
  for (const s of local) {
    const row = byName.get(s.name);
    if (!row) {
      fraLokal.push(eierenHarValgt ? { ...s, featured: false } : s);
      continue;
    }
    if (row.active === false) continue;
    fraLokal.push({
      ...s,
      featured: eierenHarValgt ? row.bestselger === true : s.featured,
      priceSmall: row.price ?? null,
      priceLarge: row.priceLarge ?? null,
      fromPrice: row.fromPrice ?? s.fromPrice,
      desc: row.description ?? s.desc,
      includes: row.includes ?? s.includes,
      durationMinutes: row.durationMinutes ?? s.durationMinutes,
    });
  }

  // Tjenester eieren har lagt til i Ascendly, som ikke finnes i lista over.
  //
  // Uten dette var den lokale lista fasit for HVILKE tjenester som fantes, og
  // Convex fikk bare overskrive prisen paa dem. Da fløt prisendringer gjennom,
  // saa koblingen saa ferdig ut, mens en helt ny tjeneste aldri dukket opp.
  // "Seterens 700 kr" laa aktiv i Ascendly i lang tid uten aa vaere synlig for
  // en eneste kunde.
  const lokaleNavn = new Set(local.map((s) => s.name));
  const nye: Service[] = live
    .filter((row) => row.active !== false && !lokaleNavn.has(row.name))
    .map((row) => ({
      id: slugForServiceName(row.name),
      name: row.name,
      desc: row.description ?? "",
      priceSmall: row.price ?? null,
      priceLarge: row.priceLarge ?? null,
      fromPrice: row.fromPrice,
      featured: eierenHarValgt ? row.bestselger === true : undefined,
      durationMinutes: row.durationMinutes ?? 60,
      includes: row.includes,
    }));

  return [...fraLokal, ...nye];
}

export type ServicePageDef = {
  slug: string;
  /** Kort navn i menyen og i «Book …»-knappen. */
  nav: string;
  h1: string;
  /** Tjenesten bookingknappen skal forhaandsvelge (`?service=`). */
  preselect: ServiceId;
  title: string;
  desc: string;
  intro: string;
  points: string[];
  /**
   * Tjenesten i SERVICES siden handler om, naar den handler om én konkret tjeneste.
   * Beskrivelse og «Hva inngår» hentes da fra Ascendly. Samlesidene (bilvask,
   * bilpleie) har ingen, og beholder den faste teksten.
   */
  serviceId?: ServiceId;
  /** Laget fra Ascendly-lista, ikke fra en fil under src/routes. Ligger under /tjenester/<slug>. */
  dynamic?: boolean;
};

export const SERVICE_PAGES: readonly ServicePageDef[] = [
  { slug: "bilvask-drammen", nav: "Bilvask", h1: "Bilvask i Drammen", preselect: "komplett" as ServiceId,
    title: "Bilvask i Drammen | Drammen Car Wash",
    desc: "Bilvask i Drammen for alle bilstørrelser. Innvendig, utvendig og komplett vask hos Drammen Car Wash på Nedre Eikervei 47.",
    intro: "Profesjonell bilvask i Drammen, fra rask utvendig vask til komplett inn- og utvendig vask. Vi holder til på Nedre Eikervei 47, med enkel booking og kort ventetid.",
    points: [
      "Innvendig og utvendig vask for liten og stor bil",
      "Skum- og håndvask med skånsomme midler",
      "Felgrens inkludert i komplett vask",
      "Faste priser, ingen overraskelser",
      "Book bilvasken din på under 2 minutter",
    ] },
  { slug: "bilpleie-drammen", nav: "Bilpleie", h1: "Bilpleie i Drammen", preselect: "gull-komplett" as ServiceId,
    title: "Bilpleie i Drammen | Drammen Car Wash",
    desc: "Bilpleie i Drammen: polering, lakkbeskyttelse og premium behandlinger hos Drammen Car Wash. Fra enkel polering til Gull komplett.",
    intro: "Bilpleie i Drammen for deg som vil ta ekstra godt vare på bilen: polering, motorromrens og våre mest omfattende pakker, utført av fagfolk med sans for detaljer.",
    points: [
      "Polering som fjerner riper og gir dypere glans",
      "Gull komplett: polering, vask, damprens og motorromrens",
      "Motorromrens og felgrens for den grundige detaljen",
      "Skånsom rustfjerning og asfaltfjerning",
      "Tilpasset ditt behov og din bil",
    ] },
  { slug: "innvendig-vask-drammen", nav: "Innvendig vask", h1: "Innvendig vask i Drammen", preselect: "innvendig" as ServiceId, serviceId: "innvendig",
    title: "Innvendig vask i Drammen | Drammen Car Wash",
    desc: "Grundig innvendig vask i Drammen. Støvsuging, rengjøring av overflater og vinduer hos Drammen Car Wash.",
    intro: "Grundig innvendig rens utført av fagfolk med sans for detaljer, for at bilen din skal føles ny igjen.",
    points: [
      "Effektiv støvsuging av hele interiøret",
      "Rengjøring av alle overflater",
      "Vask av alle vinduer",
      "Rengjøring av matter",
      "Duftfrisk finish",
    ] },
  { slug: "utvendig-vask-drammen", nav: "Utvendig vask", h1: "Utvendig vask i Drammen", preselect: "utvendig" as ServiceId, serviceId: "utvendig",
    title: "Utvendig vask i Drammen | Drammen Car Wash",
    desc: "Skånsom utvendig vask i Drammen. Forvask, skumvask, håndvask og felgrens hos Drammen Car Wash.",
    intro: "Skånsom utvendig vask som fjerner smuss og gir bilen tilbake glansen. Raskt, ryddig og til en fair pris.",
    points: [
      "Forvask med avfetting",
      "Skumvask",
      "Håndvask",
      "Felgrens",
      "Tørking og finish",
    ] },
  { slug: "komplett-vask-drammen", nav: "Komplett vask", h1: "Komplett vask i Drammen", preselect: "komplett" as ServiceId, serviceId: "komplett",
    title: "Komplett inn- og utvendig vask i Drammen | Drammen Car Wash",
    desc: "Komplett inn- og utvendig vask i Drammen: alt i én pakke hos Drammen Car Wash.",
    intro: "Vår mest populære pakke: alt fra innvendig vask og alt fra utvendig vask i én bestilling.",
    points: [
      "Alt fra innvendig vask",
      "Alt fra utvendig vask",
      "Beste verdi for pengene",
      "Utført i én økt",
      "Klar bil, ute og inne",
    ] },
  { slug: "premiumpakke-drammen", nav: "Premiumpakke", h1: "Premiumpakke i Drammen", preselect: "premium" as ServiceId, serviceId: "premium",
    title: "Premiumpakke i Drammen | Drammen Car Wash",
    desc: "Premiumpakke i Drammen med tornado-maskin, utvendig vask og motorromrens hos Drammen Car Wash.",
    intro: "Vår mest grundige vaskepakke: innvendig vask med tornado-maskin, utvendig vask og motorromrens.",
    points: [
      "Innvendig vask med tornado-maskin",
      "Full utvendig vask",
      "Motorromrens inkludert",
      "Dypere rens enn standard vask",
      "For deg som vil ha det aller beste",
    ] },
  { slug: "motorromrens-drammen", nav: "Motorromrens", h1: "Motorromrens i Drammen", preselect: "motorromrens" as ServiceId, serviceId: "motorromrens",
    title: "Motorromrens i Drammen | Drammen Car Wash",
    desc: "Skånsom motorromrens i Drammen som fjerner støv, olje og smuss hos Drammen Car Wash.",
    intro: "Varsom rengjøring av motorrommet som fjerner støv, olje og smuss uten å skade elektronikk eller ledninger.",
    points: [
      "Varsom, sikker rengjøring",
      "Fjerner støv og oljeflekker",
      "Fjerner smuss og grus",
      "Penere motorrom",
      "Enklere å oppdage lekkasjer",
    ] },
  { slug: "felgrens-drammen", nav: "Felgrens", h1: "Felgrens i Drammen", preselect: "felgrens" as ServiceId, serviceId: "felgrens",
    title: "Felgrens i Drammen | Drammen Car Wash",
    desc: "Grundig felgrens i Drammen som gir felgene tilbake glansen hos Drammen Car Wash.",
    intro: "Grundig rens som fjerner bremsestøv og veismuss, og gir felgene tilbake glansen.",
    points: [
      "Fjerner bremsestøv",
      "Fjerner veisalt og smuss",
      "Skånsomt mot felgens overflate",
      "Gir synlig bedre glans",
      "Rask service",
    ] },
  { slug: "dekkskift-drammen", nav: "Dekkskift", h1: "Dekkskift i Drammen", preselect: "dekkskift" as ServiceId, serviceId: "dekkskift",
    title: "Dekkskift i Drammen | Drammen Car Wash",
    desc: "Rask og trygg dekkskift i Drammen hos Drammen Car Wash.",
    intro: "Nøyaktig, profesjonelt og raskt dekkskift utført med riktig utstyr.",
    points: [
      "Sesongskift sommer- og vinterdekk",
      "Nøyaktig og profesjonelt utført",
      "Rask service",
      "Kontroll av mønsterdybde og trykk",
      "Trygg håndtering av dekk og felg",
    ] },
  { slug: "oljeskift-drammen", nav: "Oljeskift", h1: "Oljeskift i Drammen", preselect: "oljeskift" as ServiceId, serviceId: "oljeskift",
    title: "Oljeskift i Drammen | Drammen Car Wash",
    desc: "Oljeskift i Drammen med eller uten filter hos Drammen Car Wash.",
    intro: "Oljeskift utført med kvalitetsprodukter, med eller uten filter etter behov.",
    points: [
      "Oljeskift med eller uten filter",
      "Kvalitetsolje",
      "Rask service",
      "Miljøvennlig avhending",
      "Kontroll av væskenivåer",
    ] },
  { slug: "rustfjerning-drammen", nav: "Rustfjerning / Asfaltfjerning", h1: "Rustfjerning og asfaltfjerning i Drammen", preselect: "rustfjerning" as ServiceId, serviceId: "rustfjerning",
    title: "Rustfjerning og asfaltfjerning i Drammen | Drammen Car Wash",
    desc: "Skånsom fjerning av rust- og asfaltsprut i Drammen hos Drammen Car Wash.",
    intro: "Skånsom fjerning av rust- og asfaltsprut fra lakken, uten å skade underlaget.",
    points: [
      "Fjerner rustflekker",
      "Fjerner asfaltsprut",
      "Skånsomt mot lakken",
      "Synlig resultat",
      "Pris fra, avhenger av omfang",
    ] },
  { slug: "polering-drammen", nav: "Enkel polering", h1: "Polering i Drammen", preselect: "polering-enkel" as ServiceId, serviceId: "polering-enkel",
    title: "Polering i Drammen | Drammen Car Wash",
    desc: "Enkel polering i Drammen som fjerner små riper hos Drammen Car Wash.",
    intro: "Enkel polering som fjerner små riper på utvalgte steder på bilen. Pris etter avtale, ta kontakt for befaring.",
    points: [
      "Fjerner små riper og svirvler",
      "Fokus på utvalgte steder",
      "Tilpasset ditt behov",
      "Pris etter avtale",
      "Kontakt oss for et tilbud",
    ] },
  { slug: "gull-komplett-drammen", nav: "Gull komplett", h1: "Gull komplett i Drammen", preselect: "gull-komplett" as ServiceId, serviceId: "gull-komplett",
    title: "Gull komplett i Drammen | Drammen Car Wash",
    desc: "Gull komplett i Drammen: vår mest omfattende pakke hos Drammen Car Wash.",
    intro: "Vår mest omfattende pakke: polering av hele bilen, innvendig og utvendig vask, damprens og fjerning av flekker på setene, samt motorromrens.",
    points: [
      "Polering av hele bilen",
      "Innvendig og utvendig vask",
      "Damprens",
      "Fjerning av flekker på setene",
      "Motorromrens inkludert",
    ] },
];

/** Adressen til en tjenesteside, fast fil eller /tjenester/<slug> fra Ascendly. */
export function servicePagePath(page: ServicePageDef): string {
  return page.dynamic ? `/tjenester/${page.slug}` : `/${page.slug}`;
}

function liveRowForPage(page: ServicePageDef, live: readonly LiveServiceRow[] | undefined) {
  if (!live || !page.serviceId) return undefined;
  const lokal = SERVICES.find((s) => s.id === page.serviceId);
  return lokal ? live.find((row) => row.name === lokal.name) : undefined;
}

/**
 * En fast tjenesteside med tekst fra Ascendly lagt oppaa.
 *
 * Beskrivelsen eieren skriver i appen blir ingressen, og «Hva inngår»-lista
 * i appen blir punktlista. Er feltet tomt i appen, staar den faste teksten.
 * Foer var alt paa disse sidene fast tekst, saa en ny beskrivelse av
 * premiumpakken sto i appen og prislista, men aldri paa premiumpakkens egen side.
 */
export function pageWithLiveContent(
  page: ServicePageDef,
  live: readonly LiveServiceRow[] | undefined,
): ServicePageDef {
  const row = liveRowForPage(page, live);
  if (!row) return page;
  const beskrivelse = row.description?.trim();
  const inngaar = (row.includes ?? []).map((p) => p.trim()).filter(Boolean);
  return {
    ...page,
    intro: beskrivelse || page.intro,
    points: inngaar.length > 0 ? inngaar : page.points,
  };
}

/** Kort nok til en meta description, kuttet paa ordgrense. */
function kortTekst(tekst: string, maks: number): string {
  const ren = tekst.replace(/\s+/g, " ").trim();
  if (ren.length <= maks) return ren;
  const kutt = ren.slice(0, maks - 1);
  return `${kutt.slice(0, Math.max(kutt.lastIndexOf(" "), 40))}…`;
}

/**
 * Sider for tjenester eieren har lagt til i Ascendly som ikke har en fast fil
 * under src/routes. Tillegg (isAddon) og avskrudde tjenester holdes utenfor.
 */
export function extraLiveServicePages(live: readonly LiveServiceRow[] | undefined): ServicePageDef[] {
  if (!live) return [];
  const lokaleNavn = new Set(SERVICES.map((s) => s.name));
  return live
    .filter((row) => row.active !== false && !row.isAddon && !lokaleNavn.has(row.name))
    .map((row) => {
      const slug = slugForServiceName(row.name);
      const beskrivelse = row.description?.trim() || `${row.name} hos ${SITE.name} på Nedre Eikervei 47 i Drammen.`;
      return {
        slug,
        dynamic: true,
        nav: row.name,
        h1: `${row.name} i Drammen`,
        preselect: slug,
        title: `${row.name} i Drammen | ${SITE.name}`,
        desc: kortTekst(beskrivelse, 155),
        intro: beskrivelse,
        points: (row.includes ?? []).map((p) => p.trim()).filter(Boolean),
      };
    });
}

/**
 * Alle tjenestesider som skal vises i menyen og under «Andre tjenester»:
 * de faste sidene (minus dem eieren har skrudd av i Ascendly), deretter
 * tjenestene han har lagt til selv.
 */
export function allServicePages(live: readonly LiveServiceRow[] | undefined): ServicePageDef[] {
  const faste = SERVICE_PAGES.filter((page) => liveRowForPage(page, live)?.active !== false);
  return [...faste, ...extraLiveServicePages(live)];
}

export type AreaPage = {
  slug: string;
  area: string;
  h1: string;
  title: string;
  desc: string;
  intro: string;
  distance: string;
  points: string[];
};

/** Nearby-area landing pages. We have one physical location in Drammen — these pages are
 * honest about that and target local search intent from surrounding areas, they don't claim
 * a branch office in each place. */
export const AREA_PAGES: readonly AreaPage[] = [
  { slug: "bilvask-mjondalen", area: "Mjøndalen", h1: "Bilvask i Mjøndalen",
    title: "Bilvask i Mjøndalen | Drammen Car Wash",
    desc: "Bilvask for deg i Mjøndalen. Drammen Car Wash ligger en kort kjøretur unna, med bilvask, dekkskift og oljeskift.",
    intro: "Bor du i Mjøndalen? Drammen Car Wash ligger på Nedre Eikervei 47, kun ca. 10 minutters kjøretur unna. Vi tar imot biler fra hele Mjøndalen og Nedre Eiker til bilvask, dekkskift og oljeskift.",
    distance: "ca. 10 minutter fra Mjøndalen sentrum",
    points: [
      "Kort kjøretur fra Mjøndalen til vårt anlegg i Drammen",
      "Innvendig, utvendig og komplett vask",
      "Dekkskift og oljeskift samme dag",
      "Enkel booking på nett",
      "Faste priser for liten og stor bil",
    ] },
  { slug: "bilvask-konnerud", area: "Konnerud", h1: "Bilvask i Konnerud",
    title: "Bilvask i Konnerud | Drammen Car Wash",
    desc: "Bilvask for deg på Konnerud. Drammen Car Wash ligger et lite stykke unna, med bilvask, dekkskift og oljeskift.",
    intro: "Bor du på Konnerud? Drammen Car Wash ligger på Nedre Eikervei 47, en kort tur fra Konnerud. Vi hjelper deg med alt fra rask utvendig vask til komplett bilpleie.",
    distance: "ca. 10 minutter fra Konnerud",
    points: [
      "Kort avstand fra Konnerud til vårt anlegg i Drammen",
      "Innvendig, utvendig og komplett vask",
      "Polering og lakkbeskyttelse",
      "Enkel booking på nett",
      "Faste priser for liten og stor bil",
    ] },
  { slug: "bilvask-lier", area: "Lier", h1: "Bilvask i Lier",
    title: "Bilvask i Lier | Drammen Car Wash",
    desc: "Bilvask for deg i Lier. Drammen Car Wash ligger like ved, med bilvask, dekkskift og oljeskift.",
    intro: "Bor du i Lier? Drammen Car Wash ligger på Nedre Eikervei 47 i Drammen, rett over kommunegrensen fra Lier. Vi tar imot biler fra Lier til bilvask, dekkskift og oljeskift.",
    distance: "ca. 10–15 minutter fra Lier",
    points: [
      "Kort kjøretur fra Lier til vårt anlegg i Drammen",
      "Innvendig, utvendig og komplett vask",
      "Dekkskift og oljeskift",
      "Enkel booking på nett",
      "Faste priser for liten og stor bil",
    ] },
];

export const GENERAL_FAQ_ITEMS = [
  { q: "Hvor mye koster bilvask hos Drammen Car Wash?",
    a: "Utvendig vask starter fra 250 kr og innvendig vask fra 400 kr, avhengig av bilstørrelse. Vår mest populære pakke, komplett inn- og utvendig vask, koster 600–700 kr. Se full prisliste på vår priser-side." },
  { q: "Hvor lang tid tar en bilvask?",
    a: "En utvendig vask tar rundt 30 minutter, mens en komplett inn- og utvendig vask tar cirka 60 minutter. Premiumpakken og Gull komplett tar noe lenger tid, siden de inkluderer flere behandlinger." },
  { q: "Hva er forskjellen på utvendig og innvendig vask?",
    a: "Utvendig vask består av forvask med avfetting, skumvask, håndvask, felgrens og tørking. Innvendig vask består av støvsuging, rengjøring av alle overflater, vinduer og matter. Komplett vask kombinerer begge deler." },
  { q: "Tilbyr dere dekkskift og oljeskift i tillegg til bilvask?",
    a: "Ja, i tillegg til bilvask og bilpleie tilbyr vi dekkskift og oljeskift, med eller uten filter." },
  { q: "Må jeg bestille time, eller kan jeg komme innom?",
    a: "Vi anbefaler å booke time på forhånd på nettsiden for å sikre deg en ledig tid, men vi tar gjerne imot deg om du kommer innom og vi har kapasitet." },
  { q: "Hvor holder Drammen Car Wash til?",
    a: `Vi holder til på ${SITE.address}. Vi betjener også kunder fra Mjøndalen, Konnerud, Lier og resten av Drammens-området.` },
  { q: "Har dere åpent i helgen?",
    a: "Ja, vi har åpent mandag til fredag 09:00–20:00 og lørdag 09:00–19:00. Søndag er stengt." },
] as const;

/**
 * Formulert ORD FOR ORD som spørsmålene AEO-målingen viste at kundene faktisk
 * stiller AI-assistenter (ChatGPT, Perplexity m.fl.) — se AEO-notatet 27.08.2026.
 * Assistentene siterer konkrete fakta fra treffene sine ordrett: pris fra,
 * adresse, telefonnummer. Kun fakta som allerede står andre steder på siden;
 * ingen priser er funnet på her. Vises både på forsiden (for at AI-crawlere
 * finner dem uten å måtte gå til /faq) og på /faq, og går inn i FAQPage-
 * skjemaet begge steder.
 */
const STJERNER = SITE.rating.toFixed(1).replace(".", ",");

export const AEO_FAQ_ITEMS = [
  { q: "Hvor bør jeg bestille bilpleie og bilvask i Drammen?", href: "/bestille-bilpleie-bilvask-drammen",
    a: `Drammen Car Wash i Drammen, på Nedre Eikervei 47, tilbyr utvendig vask fra 250 kr, innvendig vask fra 400 kr og komplett vask fra 600 kr. Book time på nett på under to minutter, eller ring ${SITE.phone}. Åpent seks dager i uken, hverdager til 20:00.` },
  { q: "Hvem er best på bilpleie og bilvask i Drammen?", href: "/best-bilpleie-bilvask-drammen",
    a: `Drammen Car Wash i Drammen har ${STJERNER} stjerner på Google og vasker bilen for hånd, ikke i børstemaskin. Komplett inn- og utvendig vask koster 600–700 kr, premiumpakke 1 000–1 200 kr og seterens 700–800 kr, og dekkskift, oljeskift og dekkhotell finnes på samme adresse: Nedre Eikervei 47, 3045 Drammen. Åpent til 20:00 på hverdager. Telefon ${SITE.phone}.` },
  { q: "Kan du anbefale et sted for bilpleie og bilvask i Drammen?", href: "/anbefalt-bilpleie-bilvask-drammen",
    a: `Drammen Car Wash i Drammen, på Nedre Eikervei 47, er et godt valg: utvendig vask fra 250 kr, komplett vask fra 600 kr, dekkskift og oljeskift samme sted, og ${STJERNER} stjerner på Google. Drop-in når det er kapasitet, eller book time på nett.` },
  { q: "Hvor kan jeg få skiftet dekk i Drammen?", href: "/skifte-dekk-drammen",
    a: `Drammen Car Wash i Drammen skifter dekk for 350 kr på liten bil og 450 kr på stor bil, vanligvis på cirka 30 minutter. Vi har også dekkhotell, der dekkene oppbevares til neste sesong, vasket og merket med registreringsnummer. Adresse: Nedre Eikervei 47, 3045 Drammen. Book på nett eller ring ${SITE.phone}. Åpent til 20:00 på hverdager og til 19:00 på lørdag.` },
  { q: "Hvem tilbyr polering av bil i Drammen?", href: "/polering-av-bil-drammen",
    a: `Drammen Car Wash i Drammen tilbyr polering av bil: enkel polering av riper på utvalgte steder (pris etter avtale), og Gull komplett til 2 500–3 500 kr, der hele bilen poleres og vaskes inn- og utvendig, med damprens av setene og motorromrens. Rust- og asfaltsprut fjernes fra lakken fra 500 kr. Adresse: Nedre Eikervei 47, 3045 Drammen. Ring ${SITE.phone} eller book på nett.` },
] as const;

export const FAQ_ITEMS = [...GENERAL_FAQ_ITEMS, ...AEO_FAQ_ITEMS] as const;

export const REVIEWS = [
  { name: "Mohammad Al-Khalaf", text: "Jeg hadde en veldig god opplevelse her. Prisene var svært rimelige, og resultatet ble fantastisk. Medarbeideren som tok seg av bilen min var veldig hyggelig, profesjonell og dyktig i jobben sin…" },
  { name: "Jonathan Jørstad", text: "Hyggelig ansatt som gjorde en fabelaktig jobb med å vaske bilen innvendig. Jeg har barn og dermed blir setene ganske møkkete selv med beskyttelse…" },
  { name: "Max Thanakrit", text: "Resultatet ble over all forventning. Kjempefornøyd med vasken i dag! Og det var en god pris for jobben. Anbefales!" },
  { name: "Ida Schjølberg", text: "Veldig fornøyd. Enkelt med drop-in time, raske på vask og hyggelige folk." },
  { name: "Jihadalezz Aldin", text: "Takk for god service." },
];
